/**
 * NotesPage - All activities and notes in a searchable/filterable format
 */

import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  Eye,
  Calendar,
  CalendarX2,
  BarChart3,
  User,
  MessageSquare,
  Calculator,
  Edit3,
  Eraser,
  SlidersHorizontal,
  Trash2,
  ChevronDown,
  X,
  FolderCog,
  FolderX,
} from 'lucide-react';
import { RowsSkeleton } from '../shared/Skeleton';
import { useForecastV3 } from '../../context/ForecastV3Context';
import { useVersionEvents } from '../../hooks/useAccountEvents';
import { formatEventDescription, isAutoEvent } from '../../api/events';
import type { ForecastEvent, ForecastEventType } from '../../api/events';

// Event type display config
const EVENT_TYPE_CONFIG: Record<ForecastEventType, { label: string; icon: React.ComponentType<{ size?: number; className?: string }> }> = {
  DRIVER_CHANGED: { label: 'Driver Changed', icon: Calculator },
  OVERRIDE_CREATED: { label: 'Override Created', icon: Edit3 },
  OVERRIDE_UPDATED: { label: 'Override Updated', icon: Edit3 },
  OVERRIDE_CLEARED: { label: 'Override Cleared', icon: Eraser },
  ADJUSTMENT_CREATED: { label: 'Adjustment Created', icon: SlidersHorizontal },
  ADJUSTMENT_UPDATED: { label: 'Adjustment Updated', icon: SlidersHorizontal },
  ADJUSTMENT_DELETED: { label: 'Adjustment Deleted', icon: Trash2 },
  DAILY_VALUES_CREATED: { label: 'Daily Values Created', icon: Calendar },
  DAILY_VALUES_UPDATED: { label: 'Daily Values Updated', icon: Calendar },
  DAILY_VALUES_CLEARED: { label: 'Daily Values Cleared', icon: CalendarX2 },
  DAILY_VALUES_SPREAD: { label: 'Daily Values Spread', icon: BarChart3 },
  DEPARTMENT_DRIVER_SET: { label: 'Dept Driver Set', icon: FolderCog },
  DEPARTMENT_DRIVER_UPDATED: { label: 'Dept Driver Updated', icon: FolderCog },
  DEPARTMENT_DRIVER_CLEARED: { label: 'Dept Driver Cleared', icon: FolderX },
  NOTE: { label: 'Note', icon: MessageSquare },
};

// Auto activity types (for grouping)
const AUTO_ACTIVITY_TYPES: ForecastEventType[] = [
  'DRIVER_CHANGED',
  'OVERRIDE_CREATED',
  'OVERRIDE_UPDATED',
  'OVERRIDE_CLEARED',
  'ADJUSTMENT_CREATED',
  'ADJUSTMENT_UPDATED',
  'ADJUSTMENT_DELETED',
  'DAILY_VALUES_CREATED',
  'DAILY_VALUES_UPDATED',
  'DAILY_VALUES_CLEARED',
  'DAILY_VALUES_SPREAD',
  'DEPARTMENT_DRIVER_SET',
  'DEPARTMENT_DRIVER_UPDATED',
  'DEPARTMENT_DRIVER_CLEARED',
];

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Format relative time
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface FilterDropdownProps {
  label: string;
  value: string | string[];
  options: { value: string; label: string }[];
  onChange: (value: string | string[]) => void;
  multi?: boolean;
}

function FilterDropdown({ label, value, options, onChange, multi = false }: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedLabel = multi
    ? (value as string[]).length > 0 ? `${(value as string[]).length} selected` : label
    : options.find(o => o.value === value)?.label || label;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:border-slate-300 bg-white transition-colors"
      >
        <span className={value && (multi ? (value as string[]).length > 0 : value !== '') ? 'text-slate-700' : 'text-slate-400'}>
          {selectedLabel}
        </span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full mt-1 left-0 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[180px] py-1">
            {!multi && (
              <button
                onClick={() => { onChange(''); setIsOpen(false); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 text-slate-400"
              >
                All
              </button>
            )}
            {options.map((option) => {
              const isSelected = multi
                ? (value as string[]).includes(option.value)
                : value === option.value;

              return (
                <button
                  key={option.value}
                  onClick={() => {
                    if (multi) {
                      const currentValues = value as string[];
                      const newValues = isSelected
                        ? currentValues.filter(v => v !== option.value)
                        : [...currentValues, option.value];
                      onChange(newValues);
                    } else {
                      onChange(option.value);
                      setIsOpen(false);
                    }
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between"
                  style={isSelected ? { color: 'var(--lodge-orange-600)', background: 'var(--lodge-orange-50)' } : { color: 'var(--lodge-onyx-700)' }}
                >
                  {option.label}
                  {isSelected && multi && <span style={{ color: 'var(--lodge-orange-400)' }}>✓</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Hierarchical Type Filter with Auto Activity group
interface TypeFilterDropdownProps {
  value: string[];
  onChange: (value: string[]) => void;
}

function TypeFilterDropdown({ value, onChange }: TypeFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAutoExpanded, setIsAutoExpanded] = useState(true);

  // Check selection states
  const allAutoSelected = AUTO_ACTIVITY_TYPES.every(t => value.includes(t));
  const someAutoSelected = AUTO_ACTIVITY_TYPES.some(t => value.includes(t));
  const notesSelected = value.includes('NOTE');

  // Get label for button
  const getLabel = () => {
    if (value.length === 0) return 'Type';
    if (value.length === 1) {
      return EVENT_TYPE_CONFIG[value[0] as ForecastEventType]?.label || value[0];
    }
    if (allAutoSelected && notesSelected) return 'All types';
    if (allAutoSelected && !notesSelected) return 'All Auto Activity';
    return `${value.length} selected`;
  };

  // Toggle all auto activity types
  const toggleAllAuto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allAutoSelected) {
      // Deselect all auto types
      onChange(value.filter(v => !AUTO_ACTIVITY_TYPES.includes(v as ForecastEventType)));
    } else {
      // Select all auto types (deduplicated)
      const combined = [...value, ...AUTO_ACTIVITY_TYPES];
      const newValue = combined.filter((v, i) => combined.indexOf(v) === i);
      onChange(newValue);
    }
  };

  // Toggle individual type
  const toggleType = (type: string) => {
    if (value.includes(type)) {
      onChange(value.filter(v => v !== type));
    } else {
      onChange([...value, type]);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:border-slate-300 bg-white transition-colors"
      >
        <span className={value.length > 0 ? 'text-slate-700' : 'text-slate-400'}>
          {getLabel()}
        </span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full mt-1 left-0 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-[220px] max-h-[400px] overflow-y-auto">
            <div className="py-1">
              {/* Auto Activity Group Header - Collapsible */}
              <button
                onClick={() => setIsAutoExpanded(!isAutoExpanded)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center justify-between font-medium"
                style={allAutoSelected
                  ? { color: 'var(--lodge-orange-600)', background: 'var(--lodge-orange-50)' }
                  : someAutoSelected
                    ? { color: 'var(--lodge-orange-400)' }
                    : { color: 'var(--lodge-onyx-700)' }}
              >
                <div className="flex items-center gap-2">
                  <ChevronDown
                    size={14}
                    className={`text-slate-400 transition-transform ${isAutoExpanded ? '' : '-rotate-90'}`}
                  />
                  <div
                    className="w-4 h-4 rounded border flex items-center justify-center"
                    style={allAutoSelected
                      ? { background: 'var(--lodge-orange-400)', borderColor: 'var(--lodge-orange-400)' }
                      : someAutoSelected
                        ? { borderColor: 'var(--lodge-orange-300)', background: 'var(--lodge-orange-100)' }
                        : { borderColor: '#cbd5e1' }}
                    onClick={toggleAllAuto}
                  >
                    {allAutoSelected && <span className="text-white text-xs">✓</span>}
                    {someAutoSelected && !allAutoSelected && <span className="text-xs" style={{ color: 'var(--lodge-orange-400)' }}>−</span>}
                  </div>
                  Auto Activity
                </div>
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">AUTO</span>
              </button>

              {/* Individual Auto Activity Types - Collapsible */}
              {isAutoExpanded && AUTO_ACTIVITY_TYPES.map((type) => {
                const config = EVENT_TYPE_CONFIG[type];
                const isSelected = value.includes(type);
                const Icon = config.icon;

                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className="w-full pl-10 pr-3 py-1.5 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                    style={isSelected ? { color: 'var(--lodge-orange-600)', background: 'color-mix(in srgb, var(--lodge-orange-50) 50%, transparent)' } : { color: 'var(--lodge-onyx-600)' }}
                  >
                    <div
                      className="w-4 h-4 rounded border flex items-center justify-center"
                      style={isSelected ? { background: 'var(--lodge-orange-400)', borderColor: 'var(--lodge-orange-400)' } : { borderColor: '#cbd5e1' }}
                    >
                      {isSelected && <span className="text-white text-xs">✓</span>}
                    </div>
                    <span style={{ color: isSelected ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-400)' }}><Icon size={14} /></span>
                    <span>{config.label}</span>
                  </button>
                );
              })}

              {/* Divider */}
              <div className="my-1 border-t border-slate-100" />

              {/* Notes Option */}
              <button
                onClick={() => toggleType('NOTE')}
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                style={notesSelected ? { color: 'var(--lodge-orange-600)', background: 'var(--lodge-orange-50)' } : { color: 'var(--lodge-onyx-700)' }}
              >
                <div className="w-[14px]" /> {/* Spacer to align with collapsed items */}
                <div
                  className="w-4 h-4 rounded border flex items-center justify-center"
                  style={notesSelected ? { background: 'var(--lodge-orange-400)', borderColor: 'var(--lodge-orange-400)' } : { borderColor: '#cbd5e1' }}
                >
                  {notesSelected && <span className="text-white text-xs">✓</span>}
                </div>
                <MessageSquare size={14} className={notesSelected ? '' : 'text-slate-400'} style={notesSelected ? { color: 'var(--lodge-orange-400)' } : undefined} />
                <span>Notes</span>
              </button>

              {/* Clear all */}
              {value.length > 0 && (
                <>
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    onClick={() => { onChange([]); setIsOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                  >
                    Clear selection
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function NotesPage() {
  const { state, setActiveTab, setPendingAccountId, setPendingDetailPanelTab, setPendingDepartment } = useForecastV3();
  const { orgId, locationId, currentVersion, year } = state;

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [userFilter, setUserFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch all events for the current version
  const { events, isLoading, isError } = useVersionEvents({
    orgId: orgId || 0,
    locationId: locationId || 0,
    versionId: currentVersion?.id || 0,
    limit: 200,
    enabled: !!orgId && !!locationId && !!currentVersion?.id,
  });

  // Get unique users for filter
  const uniqueUsers = useMemo(() => {
    const users = new Map<string, string>();
    events.forEach(event => {
      users.set(event.createdBy, event.createdByName || event.createdBy);
    });
    return Array.from(users.entries()).map(([value, label]) => ({ value, label }));
  }, [events]);

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      // Type filter
      if (typeFilter.length > 0 && !typeFilter.includes(event.type)) {
        return false;
      }

      // User filter
      if (userFilter && event.createdBy !== userFilter) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const description = formatEventDescription(event).toLowerCase();
        const userName = (event.createdByName || event.createdBy).toLowerCase();
        const typeName = EVENT_TYPE_CONFIG[event.type]?.label.toLowerCase() || '';

        if (!description.includes(query) && !userName.includes(query) && !typeName.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [events, typeFilter, userFilter, searchQuery]);

  // Handle view action - navigate to workspace with account or department panel open
  const handleViewEvent = (event: ForecastEvent) => {
    if (event.accountId) {
      // Account-level event - open account detail panel
      setPendingAccountId(event.accountId);
      setPendingDetailPanelTab('notes');
    } else if (event.departmentName && event.data.accountType) {
      // Department-level event - open department detail panel
      setPendingDepartment({
        name: event.departmentName,
        section: event.data.accountType as 'Revenue' | 'Expense',
      });
    }
    setActiveTab('workspace');
  };

  // Check if an event can be viewed in workspace (has navigation data)
  const canViewInWorkspace = (event: ForecastEvent) => {
    return event.accountId != null || (event.departmentName != null && event.data.accountType != null);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter([]);
    setUserFilter('');
  };

  const hasActiveFilters = searchQuery || typeFilter.length > 0 || userFilter;

  if (!currentVersion) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400">
        <div className="text-center">
          <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
          <p>Select a version to view notes and activity</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Notes & Activity</h1>
            <p className="text-sm text-slate-500">
              All changes and notes for {currentVersion.name}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search notes and activity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 w-64 text-sm border border-slate-200 rounded-lg focus:outline-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors"
              style={(showFilters || hasActiveFilters)
                ? { borderColor: 'var(--lodge-orange-300)', background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-600)' }
                : { borderColor: 'var(--lodge-onyx-200)', color: 'var(--lodge-onyx-600)' }}
            >
              <Filter size={16} />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full" style={{ background: 'var(--lodge-orange-400)' }} />
              )}
            </button>
          </div>
        </div>

        {/* Filters row */}
        {showFilters && (
          <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
            <TypeFilterDropdown
              value={typeFilter}
              onChange={setTypeFilter}
            />

            <FilterDropdown
              label="User"
              value={userFilter}
              options={uniqueUsers}
              onChange={(v) => setUserFilter(v as string)}
            />

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-slate-500 hover:text-slate-700 underline"
              >
                Clear filters
              </button>
            )}

            <div className="ml-auto text-sm text-slate-400">
              {filteredEvents.length} of {events.length} items
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <>
          <div className="rn-sk-inset-only"><RowsSkeleton rows={8} cols={5} label="Loading activity" /></div>
          <div className="rn-sk-classic-only flex items-center justify-center py-12">
            <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lodge-orange-400)', borderTopColor: 'transparent' }} />
            <span className="ml-2 text-sm text-slate-500">Loading activity...</span>
          </div>
          </>
        ) : isError ? (
          <div className="flex items-center justify-center py-12 text-red-500">
            <span className="text-sm">Failed to load activity</span>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <MessageSquare size={48} className="mb-4 opacity-50" />
            <p className="text-sm">
              {hasActiveFilters ? 'No results match your filters' : 'No activity yet'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-2 text-sm underline" style={{ color: 'var(--lodge-orange-500)' }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredEvents.map((event) => {
                const config = EVENT_TYPE_CONFIG[event.type] || { label: event.type, icon: MessageSquare };
                const Icon = config.icon;
                const isAuto = isAutoEvent(event.type);
                const description = formatEventDescription(event);

                return (
                  <tr key={event.id} className="hover:bg-slate-50 transition-colors">
                    {/* Type */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md ${isAuto ? 'bg-amber-100' : 'bg-slate-100'}`}>
                          <Icon size={14} className={isAuto ? 'text-amber-600' : 'text-slate-500'} />
                        </div>
                        <div>
                          <span className="text-sm text-slate-700">{config.label}</span>
                          {isAuto && (
                            <span className="ml-2 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                              AUTO
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Description */}
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700 max-w-md truncate" title={description}>
                        {description}
                      </p>
                    </td>

                    {/* User */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {event.createdByName || event.createdBy}
                        </span>
                      </div>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-slate-400" />
                        <span className="text-sm text-slate-600" title={formatDate(event.createdAt)}>
                          {formatRelativeTime(event.createdAt)}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      {canViewInWorkspace(event) && (
                        <button
                          onClick={() => handleViewEvent(event)}
                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          title="View in Workspace"
                        >
                          <Eye size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default NotesPage;
